import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/MobileLogin.css";
import api from "../context/api";

const VerifyScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { prefix, requestId, phoneNumber, otpLength: receivedOtpLength } = location.state || {};

  const [enteredOTP, setEnteredOTP] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(60); // 60 seconds countdown
  const [canResend, setCanResend] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef([]);
  
  // Use otpLength from backend response, fallback to 4 digits
  const otpLength = receivedOtpLength || 4;

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, otpLength);
  }, [otpLength]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer > 0) {
      const timerId = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timerId);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleInputChange = (text, index) => {
    const updated = enteredOTP.split("");
    updated[index] = text;
    setEnteredOTP(updated.join(""));
  };

  const handleInputKeyDown = (event, index) => {
    if (event.key === "Backspace" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const resendOTP = async () => {
    if (!requestId || !prefix) {
      alert("Verification data missing. Please restart the process.");
      navigate("/MobileLogin");
      return;
    }

    setIsResending(true);

    try {
      const response = await api.post("/otp/resend", {
        requestId,
        prefix,
      });

      if (response.data.code === "0000") {
        alert("OTP resent successfully!");
        setEnteredOTP(""); // Clear entered OTP
        setResendTimer(60); // Reset timer
        setCanResend(false);
        inputRefs.current[0]?.focus(); // Focus first input
      } else {
        alert("Failed to resend OTP: " + (response.data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      alert("An error occurred while resending OTP. Please try again later.");
    } finally {
      setIsResending(false);
    }
  };

  const verify = async () => {
    if (!requestId || !prefix) {
      alert("Verification data missing. Please restart the process.");
      navigate("/MobileLogin");
      return;
    }

    if (enteredOTP.length !== otpLength) {
      alert(`Please enter the ${otpLength}-digit code.`);
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post("/otp/verify", {
        requestId,
        prefix,
        code: enteredOTP,
      });

      if (response.status === 200) {
        alert("Verification successful!");
        // Continue your registration or login flow here
        navigate("/Register", { state: { phoneNumber } });
      } else {
        alert("Verification failed. Please try again.");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      alert("An error occurred while verifying OTP. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>Enter Verification Code</h1>
        <p>We have sent a {otpLength}-digit code to your mobile number.</p>

        <div className="otp-input-group">
          {Array.from({ length: otpLength }).map((_, index) => (
            <input
              key={index}
              ref={(ref) => (inputRefs.current[index] = ref)}
              className="otp-input"
              maxLength={1}
              type="tel"
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value.length === 1) {
                  if (index < inputRefs.current.length - 1) {
                    inputRefs.current[index + 1]?.focus();
                  }
                  handleInputChange(value, index);
                }
              }}
              onKeyDown={(e) => handleInputKeyDown(e, index)}
            />
          ))}
        </div>

        <button onClick={verify} disabled={isLoading}>
          {isLoading ? "Verifying..." : "Verify"}
        </button>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          {!canResend ? (
            <p style={{ color: '#888', fontSize: '14px' }}>
              Resend code in {resendTimer}s
            </p>
          ) : (
            <button 
              onClick={resendOTP} 
              disabled={isResending}
              style={{
                background: 'transparent',
                color: '#007bff',
                border: 'none',
                fontSize: '14px',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              {isResending ? "Resending..." : "Resend Code"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyScreen;
